import { Injectable, computed, inject, signal } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';

export type ThemeMode = 'light' | 'dark';

const THEME_STORAGE_KEY = 'mallflow_theme';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly overlayContainer = inject(OverlayContainer);

  readonly current = signal<ThemeMode>('dark');
  readonly isLight = computed(() => this.current() === 'light');

  constructor() {
    this.initialize();
  }

  initialize(): void {
    const stored = localStorage.getItem('mallflow_theme');
    const prefersLight =
      typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches;
    const theme: ThemeMode = stored === 'light' || stored === 'dark' ? stored : prefersLight ? 'light' : 'dark';
    this.current.set(theme);
    this.apply(theme);
  }

  setTheme(theme: ThemeMode): void {
    this.current.set(theme);
    this.apply(theme);
    localStorage.setItem('mallflow_theme', theme);
  }

  toggleTheme(): void {
    this.setTheme(this.current() === 'dark' ? 'light' : 'dark');
  }

  private apply(theme: ThemeMode): void {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.colorScheme = theme;
    const container = this.overlayContainer.getContainerElement();
    if (container) {
      container.classList.toggle('theme-light', theme === 'light');
      container.classList.toggle('theme-dark', theme === 'dark');
    }
  }
}